import React, { useState, useEffect, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { libraryApi } from '../../lib/api/libraryApi';
import { TrashIcon, Bars3Icon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

const assessmentCategoryOptions = [
  { value: 'sales', label: 'Sales', description: 'For SDRs, AEs, and revenue roles.' },
  { value: 'marketing', label: 'Marketing', description: 'For growth, content, and brand roles.' },
  { value: 'software-engineering', label: 'Software Engineering', description: 'For backend, frontend, and full-stack roles.' },
  { value: 'data', label: 'Data & Analytics', description: 'For data analysts, scientists, and BI roles.' },
  { value: 'product', label: 'Product Management', description: 'For PMs and product strategy roles.' },
  { value: 'recruiting', label: 'Talent & Recruiting', description: 'For recruiters and talent partners.' },
  { value: 'customer-success', label: 'Customer Success', description: 'For CS, onboarding, and account roles.' },
  { value: 'operations', label: 'Operations', description: 'For business, ops, and enablement roles.' },
  { value: 'finance', label: 'Finance', description: 'For FP&A, accounting, and finance roles.' },
  { value: 'people', label: 'People & HR', description: 'For HRBPs and people operations roles.' },
  { value: 'it', label: 'IT', description: 'For IT support, systems, and infrastructure roles.' },
  { value: 'engineering', label: 'Engineering', description: 'For engineering roles across disciplines.' },
  { value: 'general', label: 'General', description: 'A broad evaluation for mixed roles.' },
  { value: 'communication', label: 'Communication', description: 'For roles requiring strong verbal and written communication skills.' },
  { value: 'customer-support', label: 'Customer Support', description: 'For technical support, customer service, and helpdesk.' },
  { value: 'design-ux', label: 'Design & UX', description: 'For UX/UI designers, graphic designers, and researchers.' },
  { value: 'accounting', label: 'Accounting', description: 'For accountants, bookkeepers, and tax professionals.' },
  { value: 'legal', label: 'Legal', description: 'For legal counsel, paralegals, and compliance officers.' },
  { value: 'healthcare', label: 'Healthcare', description: 'For nurses, medical assistants, and healthcare administrators.' },
  { value: 'education', label: 'Education & Training', description: 'For teachers, trainers, and instructional designers.' },
  { value: 'administration', label: 'Administration', description: 'For administrative assistants, office managers, and receptionists.' },
  { value: 'project-management', label: 'Project Management', description: 'For Scrum masters, Agile coaches, and project coordinators.' },
  { value: 'business-development', label: 'Business Development', description: 'For partnerships, alliances, and B2B growth roles.' },
  { value: 'supply-chain', label: 'Supply Chain & Logistics', description: 'For procurement, inventory, and logistics managers.' },
  { value: 'quality-assurance', label: 'Quality Assurance', description: 'For manual/automation testers and QA engineers.' },
  { value: 'cybersecurity', label: 'Cybersecurity', description: 'For security analysts, penetration testers, and compliance.' },
];

const MultiModuleCreate = ({
  isModal,
  jobPostingId,
  screeningName,
  registerSubmit,
  onLoadingChange,
  onGenerationStart,
  onValidationChange,
  onGeneratedSections,
  onCreated,
  initialDraft,
  onDraftChange,
  viewOnly = false,
  modules: initialModules = [],
}) => {
  const [title, setTitle] = useState(initialDraft?.title || (screeningName ? `${screeningName} Multi-Module Assessment` : ''));
  const [category, setCategory] = useState(initialDraft?.category || '');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [modules, setModules] = useState(
    initialModules.length > 0
      ? initialModules.map((m, idx) => {
          const liveData = (typeof m.moduleTestId === 'object' && m.moduleTestId) || (typeof m.libraryTestId === 'object' && m.libraryTestId) || {};
          const liveQuestionsCount =
            liveData.numberOfQuestions ??
            (Array.isArray(liveData.questions) && liveData.questions.length > 0 ? liveData.questions.length : undefined) ??
            (Array.isArray(m.questionBank) && m.questionBank.length > 0 ? m.questionBank.length : undefined) ??
            m.questionCount ??
            m.questionsToPick;
          const livePoints =
            (liveData.totalPoints > 0 ? liveData.totalPoints : undefined) ??
            (Array.isArray(liveData.questions) && liveData.questions.length > 0 ? liveData.questions.reduce((sum, q) => sum + (q.points || 1), 0) : undefined) ??
            (Array.isArray(m.questionBank) && m.questionBank.length > 0 ? m.questionBank.reduce((sum, q) => sum + (q.points || 1), 0) : undefined) ??
            m.points ??
            m.totalPoints;
          return {
            ...m,
            id: String(m._id || m.id || `mod_${idx}`),
            libraryTestId: m.libraryTestId?._id || m.libraryTestId || m.moduleTestId,
            duration: liveData.estimatedTime ?? m.duration ?? 0,
            questionCount: liveQuestionsCount ?? 0,
            points: livePoints ?? 0,
            title: liveData.title ?? m.title,
          };
        })
      : (initialDraft?.modules || [])
  );

  useEffect(() => {
    if (viewOnly && initialDraft) {
      if (initialDraft.title) setTitle(initialDraft.title);
      if (initialDraft.category) setCategory(initialDraft.category);
    }
  }, [initialDraft, viewOnly]);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [libraryAssessments, setLibraryAssessments] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');
  
  const isValid = viewOnly ? true : (title.trim().length > 0 && category && modules.length >= 2);

  useEffect(() => {
    if (!viewOnly) {
      onValidationChange?.(isValid);
    }
  }, [isValid, onValidationChange, viewOnly]);

  useEffect(() => {
    if (!viewOnly) {
      onDraftChange?.({ title, category, modules });
    }
  }, [title, category, modules, onDraftChange, viewOnly]);

  // Update internal modules if props change (e.g., loaded from library)
  useEffect(() => {
    if (initialModules && initialModules.length > 0) {
      setModules(initialModules.map((m, idx) => {
        const liveData = (typeof m.moduleTestId === 'object' && m.moduleTestId) || (typeof m.libraryTestId === 'object' && m.libraryTestId) || {};
        const liveQuestionsCount =
          liveData.numberOfQuestions ??
          (Array.isArray(liveData.questions) && liveData.questions.length > 0 ? liveData.questions.length : undefined) ??
          (Array.isArray(m.questionBank) && m.questionBank.length > 0 ? m.questionBank.length : undefined) ??
          m.questionCount ??
          m.questionsToPick;
        const livePoints =
          (liveData.totalPoints > 0 ? liveData.totalPoints : undefined) ??
          (Array.isArray(liveData.questions) && liveData.questions.length > 0 ? liveData.questions.reduce((sum, q) => sum + (q.points || 1), 0) : undefined) ??
          (Array.isArray(m.questionBank) && m.questionBank.length > 0 ? m.questionBank.reduce((sum, q) => sum + (q.points || 1), 0) : undefined) ??
          m.points ??
          m.totalPoints;
        return {
          ...m,
          id: String(m._id || m.id || `mod_${idx}`),
          libraryTestId: m.libraryTestId?._id || m.libraryTestId || m.moduleTestId,
          duration: liveData.estimatedTime ?? m.duration ?? 0,
          questionCount: liveQuestionsCount ?? 0,
          points: livePoints ?? 0,
          title: liveData.title ?? m.title,
        };
      }));
    }
  }, [initialModules]);

  const loadLibrary = async () => {
    setLibraryLoading(true);
    try {
      const res = await libraryApi.getAssessmentsStats({ limit: 100, search: librarySearch });
      // Filter out multi-module assessments (no nested multi-modules)
      const valid = (res.assessments || []).filter(a => !a.isMultiModule);
      setLibraryAssessments(valid);
    } catch (err) {
      console.error('Failed to load library assessments:', err);
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    if (isLibraryOpen && !viewOnly) {
      loadLibrary();
    }
  }, [isLibraryOpen, librarySearch, viewOnly]);

  const handleAddModule = (libAssmt) => {
    if (viewOnly) return;
    setModules([...modules, {
      id: `mod_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      libraryTestId: libAssmt._id,
      title: libAssmt.title,
      duration: libAssmt.estimatedTime,
      questionCount: libAssmt.numberOfQuestions,
      points: libAssmt.totalPoints,
    }]);
    setIsLibraryOpen(false);
  };

  const handleRemoveModule = (id) => {
    setModules(modules.filter(m => m.id !== id));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(modules);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setModules(items);
  };

  useEffect(() => {
    if (registerSubmit) {
      registerSubmit(async () => {
        if (!isValid) return false;
        onLoadingChange?.(true);

        const payload = {
          title: title.trim(),
          category: category,
          isMultiModule: true,
          modules: modules.map((m, index) => ({
            moduleTestId: m.libraryTestId || m.moduleTestId,
            order: index + 1,
            title: m.title,
            duration: m.duration,
            points: m.points || m.totalPoints || 0,
            questionsToPick: m.questionCount || m.questionsToPick || 0,
          })),
          estimatedTime: modules.reduce((sum, m) => sum + m.duration, 0),
        };

        return { finish: true, formData: payload, questions: [] };
      });
    }
  }, [registerSubmit, isValid, title, category, modules, onLoadingChange]);

  const totalDuration = useMemo(() => modules.reduce((sum, m) => sum + m.duration, 0), [modules]);
  const totalQuestions = useMemo(() => modules.reduce((sum, m) => sum + m.questionCount, 0), [modules]);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Title */}
        <div className="flex flex-col">
          <label className="block text-[15px] font-semibold text-gray-900 mb-1">
            Assessment title <span className="text-red-500">*</span>
          </label>
          <p className="text-[13px] text-gray-500 mb-3">
            Give your assessment a clear name to help you identify it later.
          </p>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            placeholder="e.g. Frontend Engineer Comprehensive Test"
            readOnly={viewOnly}
            className={`w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF] ${viewOnly ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''}`}
          />
          <p className="text-[11px] text-gray-400 mt-1.5">
            {title.length}/100 characters
          </p>
        </div>

        {/* Category */}
        <div className="relative flex flex-col">
          <label className="block text-[15px] font-semibold text-gray-900 mb-1">
            Category <span className="text-red-500">*</span>
          </label>
          <p className="text-[13px] text-gray-500 mb-3">
            Choose the closest role family so we can tailor questions to that track.
          </p>
          <button
            type="button"
            onClick={() => !viewOnly && setIsCategoryOpen((open) => !open)}
            className={`flex w-full items-center justify-between rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 ${
              viewOnly ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : 'bg-white hover:border-[#007BFF]/60 hover:bg-gray-50'
            }`}
          >
            <span className={category ? '' : 'text-gray-400'}>
              {category
                ? assessmentCategoryOptions.find((c) => c.value === category)?.label || category
                : 'Select category'}
            </span>
            <span className={`ml-2 transform text-gray-400 transition-transform duration-150 ${isCategoryOpen ? 'rotate-180' : 'rotate-0'}`}>
              ▾
            </span>
          </button>
          {isCategoryOpen && !viewOnly && (
            <div className="absolute z-20 top-[calc(100%+0.25rem)] w-full origin-top rounded-lg border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 transition-all duration-150 ease-out">
            <div className="max-h-64 overflow-y-auto py-1">
              {assessmentCategoryOptions.map((option) => {
                const selected = category === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setCategory(prev => prev === option.value ? '' : option.value);
                      setIsCategoryOpen(false);
                    }}
                    className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors ${
                      selected ? 'bg-[#007BFF]/5 text-gray-900' : 'hover:bg-gray-50 text-gray-800'
                    }`}
                  >
                    <span className="font-medium">{option.label}</span>
                    <span className="mt-0.5 text-xs text-gray-500">{option.description}</span>
                  </button>
                );
              })}
            </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Modules</h3>
          <p className="text-xs text-gray-500">Select assessments from your library and arrange their order. Each module keeps its own timer and scoring.</p>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="modulesList">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                {modules.map((m, index) => (
                  <Draggable key={m.id} draggableId={m.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm ${snapshot.isDragging ? 'shadow-md border-[#007BFF] ring-1 ring-[#007BFF]/20' : ''}`}
                      >
                        {!viewOnly && (
                          <div {...provided.dragHandleProps} className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing p-1">
                            <Bars3Icon className="w-5 h-5" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-[#007BFF] bg-[#007BFF]/10 px-2 py-0.5 rounded">Module {index + 1}</span>
                            <h4 className="text-sm font-medium text-gray-900 truncate">{m.title}</h4>
                          </div>
                          <div className="text-xs text-gray-500 mt-1 flex gap-3">
                            <span>{m.questionCount || (m.modules ? m.modules.length : 0)} questions</span>
                            <span>{m.duration} min</span>
                            <span>{m.points || m.totalPoints || 0} pts</span>
                          </div>
                        </div>
                        {!viewOnly && (
                          <button onClick={() => handleRemoveModule(m.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {!viewOnly && (
          <button
            onClick={() => setIsLibraryOpen(true)}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-[#007BFF] hover:bg-[#007BFF]/5 hover:border-[#007BFF]/30 transition-colors flex items-center justify-center gap-2"
          >
            <PlusIcon className="w-4 h-4" /> Add Module
          </button>
        )}

        {modules.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Total Duration:</span>
            <span className="text-sm font-bold text-gray-900">{totalDuration} minutes</span>
          </div>
        )}
      </div>

      {isLibraryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Select Library Assessment</h3>
              <button onClick={() => setIsLibraryOpen(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-5 border-b border-gray-100">
              <input
                type="text"
                placeholder="Search assessments..."
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {(() => {
                // Build a Set of already-selected library IDs so we can exclude them
                const selectedIds = new Set(modules.map(m => m.libraryTestId));
                const available = libraryAssessments.filter(a => !selectedIds.has(a._id));

                if (libraryLoading) {
                  return (
                    <div className="flex justify-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007BFF]"></div>
                    </div>
                  );
                }
                if (libraryAssessments.length === 0) {
                  return <div className="text-center p-8 text-gray-500 text-sm">No assessments found.</div>;
                }
                if (available.length === 0) {
                  return <div className="text-center p-8 text-gray-500 text-sm">All available assessments have already been added as modules.</div>;
                }
                return (
                  <div className="space-y-3">
                    {available.map(a => (
                      <button
                        key={a._id}
                        onClick={() => handleAddModule(a)}
                        className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-[#007BFF] hover:ring-1 hover:ring-[#007BFF]/20 transition-all flex justify-between items-center group"
                      >
                        <div>
                          <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap scrollbar-hidden w-full" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            <style dangerouslySetInnerHTML={{
                              __html: `
                              .scrollbar-hidden::-webkit-scrollbar {
                                display: none !important;
                              }
                            ` }} />
                            <h4 className="text-sm font-medium text-gray-900 shrink-0">{a.title}</h4>
                            {a.isRubricBased && (
                              <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-purple-100 text-purple-700">
                                Rubric Assessment
                              </span>
                            )}
                            {a.isMultiModule && !a.isRubricBased && (
                              <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-100 text-blue-700">
                                Multi Module
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 flex gap-3 mt-1">
                            <span>{a.category}</span>
                            <span>•</span>
                            <span>{a.numberOfQuestions} Qs</span>
                            <span>•</span>
                            <span>{a.estimatedTime} min</span>
                          </div>
                        </div>
                        <PlusIcon className="w-5 h-5 text-[#007BFF] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiModuleCreate;
